import {
  WarningAmber, Add, Close, Refresh, Remove, Save } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useApplications } from "./hooks/useApplications";

import { ApplicationCard } from "../components/ApplicationCard";

const ApplicationsPage: React.FC = () => {
  const {
    userData,
    loadApplications,
    loading,
    openCreate,
    user,
    items,
    fmtMoney,
    StatusChip,
    openEdit,
    isEdit,
    canEditIdentity,
    form,
    handleChange,
    errors,
    closeDialog,
    saveForm,
    open,
    setShowVerifyDialog,
    showVerifyDialog,
    setForm,
    setErrors,
    isSending,
    snackbar,
    setSnackbar,
    uploadConsent,
    uploadInstallment,
  } = useApplications();

  const theme = useTheme();
  const isMobile = true;

  const [filterStatus, setFilterStatus] = useState<
    "ALL" | "PENDING" | "APPROVED" | "FINISHED"
  >("APPROVED");
  const [visibleCount, setVisibleCount] = useState(5);
  const [openId, setOpenId] = useState<string | null>(null);

  const filteredItems = items.filter((row) => {
    if (filterStatus === "ALL") return true;
    return row.status === filterStatus;
  });

  const counts = {
    ALL: items.length,
    PENDING: items.filter((r) => r.status === "PENDING").length,
    APPROVED: items.filter((r) => r.status === "APPROVED").length,
    FINISHED: items.filter((r) => r.status === "FINISHED").length,
  };

  const visibleItems = filteredItems.slice(0, visibleCount);

  const toggleSx = {
    "&.Mui-selected": {
      bgcolor: "primary.main",
      color: "primary.contrastText",
      "&:hover": { bgcolor: "primary.dark" },
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Card
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <CardHeader
          title="Solicitudes de crédito"
          // subheader={`Gestiona y crea solicitudes asociadas a ${
          //   userData?.data?.fullName || "tu perfil"
          // }.`}
          action={
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                fullWidth={isMobile}
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadApplications}
                disabled={loading}
                sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, borderColor: "#e0e0e0", color: "#667788", fontSize: 13 }}
              >
                Actualizar
              </Button>
              <Button
                fullWidth={isMobile}
                variant="contained"
                startIcon={<Add />}
                onClick={openCreate}
                sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: "#00C853", color: "white", fontSize: 13, boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
              >
                Solicitar crédito
              </Button>
            </Stack>
          }
          sx={{ pb: 0 }}
        />
        <CardContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Titular: <b>{userData?.data?.fullName || "Anónimo"}</b> · Contacto:{" "}
            <b>{user?.email || user?.phoneNumber || "Sin contacto"}</b>
          </Typography>
        </CardContent>
      </Card>

      {/* Filtros modernos */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {[
          { value: "APPROVED", label: "Aprobadas", count: counts.APPROVED, color: "#00C853" },
          { value: "PENDING", label: "Pendientes", count: counts.PENDING, color: "#f59e0b" },
          { value: "FINISHED", label: "Finalizadas", count: counts.FINISHED, color: "#8899AA" },
          { value: "ALL", label: "Todas", count: counts.ALL, color: "#0D1420" },
        ].map((filter) => (
          <Box
            key={filter.value}
            onClick={() => setFilterStatus(filter.value as any)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 2, py: 0.8,
              borderRadius: "50px",
              cursor: "pointer",
              border: `2px solid ${filterStatus === filter.value ? filter.color : "#e0e0e0"}`,
              bgcolor: filterStatus === filter.value ? `${filter.color}15` : "transparent",
              transition: "all 0.2s ease",
              "&:hover": { borderColor: filter.color, bgcolor: `${filter.color}10` },
            }}
          >
            <Box sx={{ fontSize: 13, fontWeight: filterStatus === filter.value ? 700 : 500, color: filterStatus === filter.value ? filter.color : "#667788" }}>
              {filter.label}
            </Box>
            <Box sx={{ bgcolor: filterStatus === filter.value ? filter.color : "#e0e0e0", color: filterStatus === filter.value ? "white" : "#667788", borderRadius: "50px", px: 1, py: 0.1, fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: "center" }}>
              {filter.count}
            </Box>
          </Box>
        ))}
      </Box>

      <Grid container spacing={2} sx={{ maxWidth: 1600 }}>
        {visibleItems.length === 0 ? (
          <Grid item>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent
                sx={{ textAlign: "center", color: "text.secondary" }}
              >
                {loading ? "Cargando..." : "No hay información disponible"}
              </CardContent>
            </Card>
          </Grid>
        ) : (
          visibleItems.map((row) => (
            <Grid item key={row.id} xs={12} sm={6} md={4}>
              <ApplicationCard
                row={row}
                fmtMoney={fmtMoney}
                StatusChip={StatusChip}
                open={openId === row.id}
                onToggle={() => setOpenId(openId === row.id ? null : row.id!)}
                uploadConsent={uploadConsent}
                uploadInstallment={uploadInstallment}
                user={user!}
              />
            </Grid>
          ))
        )}
      </Grid>

      {/* Ver más */}
      {visibleCount < filteredItems.length && (
        <Box textAlign="center" mt={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setVisibleCount((c) => c + 5)}
          >
            Ver más
          </Button>
        </Box>
      )}

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        {/* Header */}
        <Box sx={{ bgcolor: "#0D1420", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>
            {isEdit ? "Editar solicitud" : "Nueva solicitud de crédito"}
          </Typography>
          <IconButton onClick={closeDialog} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: "#f7f9f7" }}>
          {/* Info solicitante */}
          <Box sx={{ p: 2, bgcolor: "white", borderBottom: "1px solid #f0f0f0" }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 50, height: 50, bgcolor: "#00C85322", border: "2px solid #00C853", color: "#00C853", fontWeight: 800, fontSize: 18 }}>
                {(userData.data?.fullName?.split(" ").slice(0, 2).map((n: string) => n[0]).join("") || "?")}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0D1420" }}>{userData.data?.fullName || "Anónimo"}</Typography>
                <Typography sx={{ fontSize: 12, color: "#8899AA" }}>{userData.data?.documentType} {userData.data?.document}</Typography>
                <Typography sx={{ fontSize: 12, color: "#8899AA" }}>{user?.phoneNumber}</Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            {/* Monto */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Monto solicitado</Typography>
            <TextField
              fullWidth
              placeholder="$ 0"
              value={form.amount ? fmtMoney(form.amount) : ""}
              onChange={(e) => {
                const num = Number(e.target.value.replace(/\D/g, ""));
                setForm((prev) => ({ ...prev, amount: num }));
              }}
              error={!!errors.amount}
              helperText={errors.amount || ""}
              sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 20, fontWeight: 800 } }}
            />

            {/* Plazo */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Plazo</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, bgcolor: "white", borderRadius: 2, border: "1px solid #e0e0e0", p: 1 }}>
              <IconButton size="small" onClick={() => setForm((p) => ({ ...p, termMonths: Math.max(1, (p.termMonths ?? 12) - 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
                <Remove fontSize="small" />
              </IconButton>
              <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 800, fontSize: 20, color: "#0D1420" }}>{form.termMonths ?? 12} meses</Typography>
              <IconButton size="small" onClick={() => setForm((p) => ({ ...p, termMonths: Math.min(84, (p.termMonths ?? 12) + 1) }))} sx={{ bgcolor: "#f0f0f0" }}>
                <Add fontSize="small" />
              </IconButton>
            </Box>

            {/* Propósito como chips */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Propósito</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {["Compra de vehículo", "Remodelación", "Estudios", "Pago de deudas", "Viaje", "Otros"].map((p) => (
                <Box key={p} onClick={() => setForm((prev) => ({ ...prev, purpose: p }))}
                  sx={{ px: 2, py: 0.8, borderRadius: "50px", cursor: "pointer", border: `2px solid ${form.purpose === p ? "#00C853" : "#e0e0e0"}`, bgcolor: form.purpose === p ? "#00C85315" : "white", color: form.purpose === p ? "#00C853" : "#667788", fontWeight: form.purpose === p ? 700 : 500, fontSize: 13, transition: "all 0.2s" }}>
                  {p}
                </Box>
              ))}
            </Box>
            {errors.purpose && <Typography sx={{ fontSize: 12, color: "#ef4444", mb: 1 }}>{errors.purpose}</Typography>}

            {/* Avalista */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Avalista (opcional)</Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth placeholder="Nombre del avalista" value={form.guarantorName || ""} onChange={(e) => setForm((p) => ({ ...p, guarantorName: e.target.value }))} error={!!errors.guarantorName} helperText={errors.guarantorName || ""} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth placeholder="Teléfono del avalista" value={form.guarantorPhone || ""} onChange={(e) => setForm((p) => ({ ...p, guarantorPhone: e.target.value }))} error={!!errors.guarantorPhone} helperText={errors.guarantorPhone || ""} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
            </Grid>

            {/* Info bancaria */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>Información bancaria</Typography>
            <Stack spacing={1.5}>
              <TextField select fullWidth label="Banco" value={form.accountBank || ""} onChange={handleChange("accountBank")} error={!!errors.accountBank} helperText={errors.accountBank || ""} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                {["Bancolombia", "Davivienda", "Nequi", "Daviplata", "Otros"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField select fullWidth label="Tipo de cuenta" value={form.accountType || ""} onChange={handleChange("accountType")} error={!!errors.accountType} helperText={errors.accountType || ""} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                {["Ahorros", "Corriente", "No aplica", "Otro"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField fullWidth label="Número de cuenta" value={form.accountNumber || ""} onChange={(e) => setForm((p) => ({ ...p, accountNumber: e.target.value }))} error={!!errors.accountNumber} helperText={errors.accountNumber || ""} sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            </Stack>
          </Box>
        </DialogContent>

        <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1.5, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
          <Button fullWidth onClick={closeDialog} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" startIcon={<Save />} onClick={saveForm} disabled={isSending}
            sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: "#00C853", color: "white", boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}>
            {isSending ? "Enviando..." : isEdit ? "Guardar cambios" : "Enviar solicitud"}
          </Button>
        </Box>
      </Dialog>

      <Dialog
        open={showVerifyDialog}
        onClose={() => setShowVerifyDialog(false)}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}><WarningAmber sx={{ color: "#f59e0b" }} />Usuario no verificado</DialogTitle>
        <DialogContent>
          <Typography>
            Para crear una solicitud de crédito debes ser un usuario{" "}
            <b>verificado</b>. Dirígete a <b>Datos personales</b> y completa tu
            información.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowVerifyDialog(false)}
            variant="contained"
            color="primary"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 3,
            fontWeight: 600,
            bgcolor: snackbar.severity === "success" ? "#00C853" : snackbar.severity === "error" ? "#ef4444" : snackbar.severity === "warning" ? "#f59e0b" : "#60a5fa",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action svg": { color: "white" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationsPage;
